import React, { useState, useEffect } from "react";
import { Container, Row, Col, Input, Label } from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import CustomMultiSelector from "../../../components/AuthenticationComponents/CustomMultiSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";

//Import API
import endpoints from "../../../api/endpoints";
import { get, postFormData } from "../../../api/axios";
import thumbPlaceholder from "../../../assets/images/user-dashboard/image.svg";
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * AdminRestaurantsEditPage — edit restaurant with working autocomplete and checkboxes
 * ---------------------------------------------------------------------- */

// Validation schema using Yup
const restaurantEditSchema = Yup.object().shape({
  title: Yup.string()
    .required("Restaurant name is required")
    .min(2, "Restaurant name must be at least 2 characters")
    .max(255, "Restaurant name must not exceed 255 characters"),
  legal_business_name: Yup.string()
    .trim()
    .max(255, "Legal business name must not exceed 255 characters"),
  email: Yup.string().required("Email is required").email("Please enter a valid email address"),
  mobile: Yup.string()
    .required("Phone number is required")
    .min(10, "Phone number must be at least 10 characters"),
  website: Yup.string().url("Please enter a valid website URL"),
  overview: Yup.string()
    .required("Overview is required")
    .min(10, "Overview must be at least 10 characters"),
  price_range: Yup.string().required("Price range is required"),
  address: Yup.string().required("Address is required"),
  is_member: Yup.boolean(),
});

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const createDefaultWeekHours = () =>
  DAY_NAMES.map((day) => ({ day, open: "", closed: "", isClosed: true, id: null }));

const getDayKey = (day) => day.toLowerCase();

const normalizeTime = (value) => {
  if (!value) return "";
  const [hours = "", minutes = ""] = value.toString().trim().split(":");
  if (!hours || !minutes) return "";
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

const normalizeBoolean = (value) => value === true || value === 1 || value === "1" || value === "true";

const normalizeListingTime = (listingTime) => {
  const byDay = (Array.isArray(listingTime) ? listingTime : []).reduce((acc, entry) => {
    const dayKey = entry?.day?.toString().trim().toLowerCase();
    if (dayKey) acc[dayKey] = entry;
    return acc;
  }, {});

  return createDefaultWeekHours().map((hour) => {
    const entry = byDay[getDayKey(hour.day)];
    if (!entry) return hour;

    if (normalizeBoolean(entry.is_open_24_hours)) {
      return { ...hour, open: "00:00", closed: "23:59", isClosed: false, id: entry.id || null };
    }

    const hours = entry.hours?.toString().trim();
    if (hours && hours !== "null-null") {
      const [open, closed] = hours.split("-").map((time) => normalizeTime(time));
      return {
        ...hour,
        open,
        closed,
        isClosed: normalizeBoolean(entry.is_closed) || !open || !closed,
        id: entry.id || null,
      };
    }

    const open = normalizeTime(entry.open || entry.open_time || entry.opening_time);
    const closed = normalizeTime(entry.close || entry.close_time || entry.closing_time);
    return {
      ...hour,
      open,
      closed,
      isClosed: normalizeBoolean(entry.is_closed) || !open || !closed,
      id: entry.id || null,
    };
  });
};

const serializeWeekHours = (weekHours, isOpen24Hours) =>
  weekHours.reduce((acc, hour) => {
    const dayKey = getDayKey(hour.day);
    if (isOpen24Hours) {
      acc[dayKey] = "00:00 - 23:59";
    } else if (hour.isClosed) {
      acc[dayKey] = "null-null";
    } else {
      acc[dayKey] = `${hour.open} - ${hour.closed}`;
    }
    return acc;
  }, {});

const validateWeekHours = (weekHours, isOpen24Hours) => {
  if (isOpen24Hours) return {};

  return weekHours.reduce((errors, hour) => {
    if (hour.isClosed) return errors;

    const dayErrors = {};
    if (!hour.open) dayErrors.open = "Opening time is required";
    if (!hour.closed) dayErrors.closed = "Closing time is required";

    if (Object.keys(dayErrors).length) {
      errors[getDayKey(hour.day)] = dayErrors;
    }
    return errors;
  }, {});
};

const normalizeString = (value) => value?.toString() || "";

const trimPrimaryAdminValues = (values) => ({
  firstName: values.primary_admin_first_name.trim(),
  lastName: values.primary_admin_last_name.trim(),
  email: values.primary_admin_email.trim(),
  mobile: values.primary_admin_mobile.trim(),
});

const hasPrimaryAdminInput = (primaryAdminValues) =>
  Boolean(
    primaryAdminValues.firstName ||
    primaryAdminValues.lastName ||
    primaryAdminValues.email ||
    primaryAdminValues.mobile,
  );

const mapPrimaryAdminErrors = (errors = {}) => {
  const mappedErrors = {};
  const fieldMap = {
    "admin.email": "primary_admin_email",
    "admin.password": "primary_admin_password",
    "admin.password_confirmation": "primary_admin_password_confirmation",
    "primary_admin.first_name": "primary_admin_first_name",
    "primary_admin.last_name": "primary_admin_last_name",
    "primary_admin.email": "primary_admin_email",
    "primary_admin.mobile": "primary_admin_mobile",
  };

  Object.entries(fieldMap).forEach(([apiField, formField]) => {
    const error = errors[apiField];
    if (Array.isArray(error)) {
      mappedErrors[formField] = error[0];
    } else if (typeof error === "string") {
      mappedErrors[formField] = error;
    }
  });

  ["legal_business_name", "category_id"].forEach((apiField) => {
    const error = errors?.[apiField];
    if (Array.isArray(error) && error[0]) {
      mappedErrors[apiField] = error[0];
    } else if (typeof error === "string" && error.trim()) {
      mappedErrors[apiField] = error;
    }
  });

  return mappedErrors;
};

const getFirstValidationMessage = (errors = {}) => {
  for (const value of Object.values(errors || {})) {
    if (Array.isArray(value) && value[0]) return value[0];
    if (typeof value === "string" && value.trim()) return value;
  }

  return "";
};

const getSubmitErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  const fieldMessage = getFirstValidationMessage(responseData?.errors);

  if (fieldMessage) return fieldMessage;
  if (responseData?.message) return responseData.message;
  if (typeof responseData?.error === "string" && responseData.error.trim()) return responseData.error;

  return extractErrorMessage(error) || fallback;
};

const GOOGLE_PLACES_SCRIPT_ID = "google-places-api-script";
const GOOGLE_PLACES_API_KEY_ENV = "REACT_APP_GOOGLE_PLACES_API_KEY";

const isGooglePlacesReady = () => Boolean(
  window.google
  && window.google.maps
  && window.google.maps.places
  && window.google.maps.places.Autocomplete
);

const loadGooglePlacesApi = () => {
  if (isGooglePlacesReady()) return Promise.resolve(window.google);

  const apiKey = process.env[GOOGLE_PLACES_API_KEY_ENV];
  if (!apiKey) return Promise.reject(new Error(`${GOOGLE_PLACES_API_KEY_ENV} is missing`));

  if (window.__googlePlacesApiPromise) return window.__googlePlacesApiPromise;

  window.__googlePlacesApiPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_PLACES_SCRIPT_ID)
      || Array.from(document.scripts).find((script) => script.src.includes("maps.googleapis.com/maps/api/js"));

    const handleLoad = async () => {
      if (isGooglePlacesReady()) {
        resolve(window.google);
        return;
      }

      if (window.google?.maps?.importLibrary) {
        try {
          await window.google.maps.importLibrary("places");
          if (isGooglePlacesReady()) {
            resolve(window.google);
            return;
          }
        } catch (error) {
          reject(error);
          return;
        }
      }

      reject(new Error("Google Places library did not load"));
    };

    const handleError = () => reject(new Error("Failed to load Google Places API script"));

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      if (isGooglePlacesReady() || window.google?.maps) handleLoad();
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_PLACES_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });

  return window.__googlePlacesApiPromise;
};

const getAddressComponent = (components, componentTypes, nameType = "long_name") => {
  const component = components.find((item) => (
    componentTypes.some((type) => item.types?.includes(type))
  ));

  return component?.[nameType]?.toString().trim() || "";
};

const parseGooglePlace = (place) => {
  if (!place) return null;

  const components = Array.isArray(place.address_components) ? place.address_components : [];
  const streetNumber = getAddressComponent(components, ["street_number"]);
  const route = getAddressComponent(components, ["route"]);
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ").trim();
  const formattedAddress = place.formatted_address?.toString().trim();
  const fallbackAddress = streetAddress || place.name?.toString().trim() || "";
  const location = place.geometry?.location;
  const latitude = typeof location?.lat === "function" ? location.lat() : "";
  const longitude = typeof location?.lng === "function" ? location.lng() : "";
  const postalCode = getAddressComponent(components, ["postal_code"]);
  const postalSuffix = getAddressComponent(components, ["postal_code_suffix"]);

  return {
    address: formattedAddress || fallbackAddress,
    city: getAddressComponent(components, ["locality"])
      || getAddressComponent(components, ["postal_town"])
      || getAddressComponent(components, ["sublocality", "sublocality_level_1"])
      || getAddressComponent(components, ["administrative_area_level_2"]),
    state: getAddressComponent(components, ["administrative_area_level_1"]),
    country: getAddressComponent(components, ["country"]),
    zipcode: [postalCode, postalSuffix].filter(Boolean).join("-"),
    latitude: latitude === "" ? "" : String(latitude),
    longitude: longitude === "" ? "" : String(longitude),
  };
};

const setFormikAddressFields = (formik, addressFields) => {
  if (!formik || !addressFields) return;

  Object.entries(addressFields).forEach(([field, value]) => {
    formik.setFieldValue(field, value ?? "");
  });
};

const normalizeOptionalCoordinate = (value) => (value === "" || value === null || value === undefined ? null : value);

const normalizeCategoryIds = (selectedCategories) =>
  Array.from(new Set(
    (Array.isArray(selectedCategories) ? selectedCategories : [])
      .map((category) => {
        if (category && typeof category === "object") {
          return category.value ?? category.id ?? category.category_id;
        }
        return category;
      })
      .map((categoryId) => Number(categoryId))
      .filter((categoryId) => Number.isFinite(categoryId) && categoryId > 0),
  ));

const normalizeCategoryOptions = (rawCategories = []) => (
  (Array.isArray(rawCategories) ? rawCategories : [])
    .map((category) => {
      const categoryId = Number(category?.id ?? category?.category_id ?? category?.value);
      if (!Number.isFinite(categoryId) || categoryId <= 0) return null;
      return {
        value: categoryId,
        label: category.title || category.name || category.label || `Category ${categoryId}`,
      };
    })
    .filter(Boolean)
);

const mergeCategoryOptions = (currentOptions = [], nextOptions = []) => {
  const optionsById = new Map();
  [...currentOptions, ...nextOptions].forEach((option) => {
    const optionId = Number(option?.value);
    if (Number.isFinite(optionId) && optionId > 0) {
      optionsById.set(optionId, { ...option, value: optionId });
    }
  });
  return Array.from(optionsById.values());
};

const getCompactToggleTrackStyle = (checked) => ({
  width: '32px',
  height: '18px',
  backgroundColor: checked ? '#ec421c' : '#d1d5db',
  borderRadius: '999px',
  position: 'relative',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  userSelect: 'none',
  flex: '0 0 auto',
});

const getCompactToggleThumbStyle = (checked) => ({
  width: '14px',
  height: '14px',
  backgroundColor: '#fff',
  borderRadius: '50%',
  position: 'absolute',
  top: '2px',
  left: checked ? '16px' : '2px',
  transition: 'left 0.2s',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
});

const validatePrimaryAdmin = (values, hasPrimaryOwner) => {
  const errors = {};
  const primaryAdminValues = trimPrimaryAdminValues(values);
  const shouldValidate = hasPrimaryOwner || hasPrimaryAdminInput(primaryAdminValues);

  if (!shouldValidate) return errors;

  if (!primaryAdminValues.firstName) {
    errors.primary_admin_first_name = "First name is required";
  }

  if (!primaryAdminValues.email) {
    errors.primary_admin_email = "Email is required";
  } else if (!Yup.string().email().isValidSync(primaryAdminValues.email)) {
    errors.primary_admin_email = "Please enter a valid email address";
  }

  return errors;
};

const resolveAssetUrl = (value) => {
  const path = value?.toString().trim();
  if (!path) return "";
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:") || path.startsWith("blob:")) return path;

  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  if (!baseUrl) return path;

  return `${baseUrl}/${path.replace(/^\//, "")}`;
};

const AdminRestaurantsEditPage = () => {
  document.title = "Edit Restaurant | Omaha Food Lovers";
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const id = restaurantId; // Use restaurantId from route params
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categoryError, setCategoryError] = useState("");
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [categoryPage, setCategoryPage] = useState(1);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [existingBanner, setExistingBanner] = useState(null);
  const [weekHours, setWeekHours] = useState(createDefaultWeekHours);
  const [weekHourErrors, setWeekHourErrors] = useState({});
  const [isOpen24Hours, setIsOpen24Hours] = useState(false);
  const [hasPrimaryOwner, setHasPrimaryOwner] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    legal_business_name: "",
    email: "",
    mobile: "",
    website: "",
    overview: "",
    price_range: "$$",
    address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    latitude: "",
    longitude: "",
    is_member: false,
    primary_admin_first_name: "",
    primary_admin_last_name: "",
    primary_admin_email: "",
    primary_admin_mobile: "",
  });
  const bannerInputRef = React.useRef(null);
  const addressInputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);
  const autocompleteListenerRef = React.useRef(null);
  const formikRef = React.useRef(null);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (fetchingData) return undefined;

    let cancelled = false;

    const initAutocomplete = async () => {
      try {
        await loadGooglePlacesApi();
        if (cancelled || !isGooglePlacesReady() || !addressInputRef.current) return;

        if (autocompleteListenerRef.current) {
          window.google.maps.event.removeListener(autocompleteListenerRef.current);
          autocompleteListenerRef.current = null;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['establishment', 'geocode'],
          fields: ['formatted_address', 'address_components', 'geometry', 'name'],
        });

        autocompleteRef.current = autocomplete;
        autocompleteListenerRef.current = autocomplete.addListener('place_changed', () => {
          const addressFields = parseGooglePlace(autocomplete.getPlace());
          setFormikAddressFields(formikRef.current, addressFields);
        });
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Google Places autocomplete unavailable:", error.message);
        }
      }
    };

    initAutocomplete();

    return () => {
      cancelled = true;
      if (autocompleteListenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(autocompleteListenerRef.current);
        autocompleteListenerRef.current = null;
      }
    };
  }, [fetchingData]);

  // Update preview when banner file changes
  useEffect(() => {
    if (bannerFile) {
      const previewUrl = URL.createObjectURL(bannerFile);
      setBannerPreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    } else {
      setBannerPreview(null);
    }
  }, [bannerFile]);

  // Fetch categories for multi-select with pagination
  const fetchCategories = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingCategories(true);
    } else {
      setLoadingCategories(true);
    }

    try {
      const response = await get(`${endpoints.listingCategories}?page=${pageNum}&per_page=20`);
      const categoryPayload = response.data?.data || response.data || {};
      const rawCategories = Array.isArray(categoryPayload?.data)
        ? categoryPayload.data
        : Array.isArray(categoryPayload?.categories)
          ? categoryPayload.categories
          : Array.isArray(response.data?.categories)
            ? response.data.categories
            : Array.isArray(categoryPayload)
              ? categoryPayload
              : [];
      const categoryOptions = normalizeCategoryOptions(rawCategories);

      if (isLoadMore) {
        setCategories((prev) => mergeCategoryOptions(prev, categoryOptions));
      } else {
        setCategories((prev) => mergeCategoryOptions(categoryOptions, prev));
      }

      setHasMoreCategories(categoryPayload.current_page < categoryPayload.last_page);
      setCategoryPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleLoadMoreCategories = () => {
    if (!loadingCategories && hasMoreCategories) {
      fetchCategories(categoryPage + 1, true);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type - only allow image files
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        toastr.error("Only image files (JPEG, PNG, GIF, WebP, SVG) are allowed");
        e.target.value = ''; // Clear the input
        return;
      }
      setBannerFile(file);
    }
  };

  const handleEditBanner = () => {
    bannerInputRef.current?.click();
  };

  // Fetch restaurant data on mount
  useEffect(() => {
    const fetchRestaurantData = async () => {
      setFetchingData(true);
      try {
        // First fetch categories to ensure they're available
        await fetchCategories();

        const response = await get(endpoints.adminSingleRestaurant.replace('{{listing_id}}', id));
        const data = response.data?.data || response.data;
        if (data) {
          const primaryOwner = data.primary_owner || null;
          const savedAddress = data.address || {};
          const savedCategories = Array.isArray(data.categories)
            ? data.categories
            : Array.isArray(response.data?.categories)
              ? response.data.categories
              : [];
          setHasPrimaryOwner(Boolean(primaryOwner));

          // Set form values using state
          setInitialValues({
            title: data.title || '',
            legal_business_name: data.legal_business_name || '',
            email: data.email || '',
            mobile: data.mobile || '',
            website: data.website || '',
            overview: data.overview || '',
            price_range: data.price_range || '$$',
            address: savedAddress.address || '',
            city: savedAddress.city || '',
            state: savedAddress.state || '',
            country: savedAddress.country || '',
            zipcode: savedAddress.zipcode || '',
            latitude: savedAddress.latitude || '',
            longitude: savedAddress.longitude || '',
            is_member: data.is_member === "1" || data.is_member === 1 || data.is_member === true,
            primary_admin_first_name: normalizeString(primaryOwner?.first_name),
            primary_admin_last_name: normalizeString(primaryOwner?.last_name),
            primary_admin_email: normalizeString(primaryOwner?.email),
            primary_admin_mobile: normalizeString(primaryOwner?.mobile),
          });

          setSelectedCategoryIds(normalizeCategoryIds(savedCategories));
          setCategories((prev) => mergeCategoryOptions(prev, normalizeCategoryOptions(savedCategories)));

          // Set existing banner
          setExistingBanner(resolveAssetUrl(data.banner_url));

          const normalizedWeekHours = normalizeListingTime(data.listing_time || data.listingTime);
          setWeekHours(normalizedWeekHours);

          // Set 24 hours from open_24_hours field
          setIsOpen24Hours(
            data.open_24_hours === 1 ||
            normalizedWeekHours.every(h => h.open === '00:00' && h.closed === '23:59' && !h.isClosed)
          );
        }
      } catch (error) {
        console.error("Failed to fetch restaurant data:", error);
        toastr.error("Failed to load restaurant data");
      } finally {
        setFetchingData(false);
      }
    };

    if (id) {
      fetchRestaurantData();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    const categoryIds = normalizeCategoryIds(selectedCategoryIds);
    if (!categoryIds.length) {
      setCategoryError("At least one category is required.");
      setSubmitting(false);
      return;
    }

    const hoursErrors = validateWeekHours(weekHours, isOpen24Hours);
    setWeekHourErrors(hoursErrors);
    if (Object.keys(hoursErrors).length) {
      setSubmitting(false);
      return;
    }

    setCategoryError("");
    setLoading(true);
    try {
      let response;

      // Transform week hours to API format
      const restaurantData = {
        title: values.title,
        legal_business_name: values.legal_business_name.trim(),
        mobile: values.mobile,
        email: values.email,
        website: values.website,
        overview: values.overview,
        price_range: values.price_range,
        category_id: categoryIds,
        openHours: isOpen24Hours,
        time: serializeWeekHours(weekHours, isOpen24Hours),
        social: {},
        is_member: values.is_member ? 1 : 0,
      };

      const addressData = {
        address: values.address || "",
        city: values.city || "",
        state: values.state || "",
        country: values.country || "",
        zipcode: values.zipcode || "",
        latitude: normalizeOptionalCoordinate(values.latitude),
        longitude: normalizeOptionalCoordinate(values.longitude),
      };

      const primaryAdminValues = trimPrimaryAdminValues(values);
      const shouldSubmitPrimaryAdmin = hasPrimaryOwner || hasPrimaryAdminInput(primaryAdminValues);

      const formData = new FormData();
      formData.append("form", JSON.stringify(restaurantData));
      formData.append("address", JSON.stringify(addressData));

      if (shouldSubmitPrimaryAdmin) {
        formData.append(
          "primary_admin",
          JSON.stringify({
            first_name: primaryAdminValues.firstName,
            last_name: primaryAdminValues.lastName || null,
            email: primaryAdminValues.email,
            mobile: primaryAdminValues.mobile || null,
          }),
        );
      }

      if (bannerFile) {
        formData.append("banner", bannerFile);
      }

      const url = endpoints.adminUpdateRestaurant.replace('{{listing_id}}', id);

      response = await postFormData(url, formData);

      // Check for various success indicators in the response
      const isSuccess = response.data.success || response.data.status || response.status === 200 || response.status === 201 || response.data.message?.toLowerCase().includes('updated successfully') || response.data.message?.toLowerCase().includes('updated');
      
      if (isSuccess) {
        toastr.success("Restaurant updated successfully!");
        navigate("/admin/restaurants");
      } else {
        toastr.error(response.data?.message || "Failed to update restaurant");
      }
    } catch (err) {
      console.error("Failed to update restaurant:", err);
      const primaryAdminErrors = mapPrimaryAdminErrors(err.response?.data?.errors);
      if (Object.keys(primaryAdminErrors).length) {
        setErrors(primaryAdminErrors);
        if (primaryAdminErrors.category_id) {
          setCategoryError(primaryAdminErrors.category_id);
        }
      }
      toastr.error(getSubmitErrorMessage(err, "Failed to update restaurant"));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="surface-card">
            <div className="profile-header">
              <h2 className="profile-title">Edit Restaurant</h2>
            </div>
              <Formik
                initialValues={initialValues}
                validationSchema={restaurantEditSchema}
                validate={(values) => validatePrimaryAdmin(values, hasPrimaryOwner)}
                onSubmit={handleSubmit}
                innerRef={formikRef}
                enableReinitialize={true}
              >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                    <Form>
                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="mb-4">
                            <label className="form-label">Banner Image</label>
                            <div
                              className="rounded d-flex align-items-center justify-content-center position-relative"
                              style={{
                                width: "100%",
                                height: 200,
                                backgroundColor: "#f8f9fa",
                                border: "2px dashed #dee2e6",
                                cursor: "pointer",
                                overflow: "hidden",
                              }}
                              onClick={handleEditBanner}
                            >
                              {bannerPreview ? (
                                <img
                                  src={bannerPreview}
                                  alt="Banner Preview"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : existingBanner ? (
                                <img
                                  src={existingBanner}
                                  alt="Existing Banner"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = thumbPlaceholder;
                                  }}
                                />
                              ) : (
                                <img
                                  src={thumbPlaceholder}
                                  alt="Restaurant placeholder"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              )}
                            </div>
                            <input
                              type="file"
                              ref={bannerInputRef}
                              className="d-none"
                              accept="image/*"
                              onChange={handleBannerFileChange}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="title"
                            label="Restaurant Name"
                            name="title"
                            type="text"
                            placeholder="Enter restaurant name"
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.title && !!errors.title}
                            errorText={errors.title}
                            className="white-bg"
                            autoComplete="organization"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="legal_business_name"
                            label="Legal Business Name"
                            name="legal_business_name"
                            type="text"
                            placeholder="Enter legal business name"
                            value={values.legal_business_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.legal_business_name && !!errors.legal_business_name}
                            errorText={errors.legal_business_name}
                            className="white-bg"
                            autoComplete="organization"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="email"
                            label="Email"
                            name="email"
                            type="email"
                            placeholder="Enter email address"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.email && !!errors.email}
                            errorText={errors.email}
                            className="white-bg"
                            autoComplete="email"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="mobile"
                            label="Phone Number"
                            name="mobile"
                            type="text"
                            placeholder="Enter phone number"
                            value={values.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.mobile && !!errors.mobile}
                            errorText={errors.mobile}
                            className="white-bg"
                            autoComplete="tel"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomSelector
                            id="price_range"
                            label="Price Range"
                            name="price_range"
                            placeholder="Select price range"
                            options={[
                              { value: "$", label: "$" },
                              { value: "$$", label: "$$" },
                              { value: "$$$", label: "$$$" },
                              { value: "$$$$", label: "$$$$" },
                            ]}
                            value={values.price_range}
                            onChange={(value) => setFieldValue("price_range", value)}
                            onBlur={handleBlur}
                            invalid={touched.price_range && !!errors.price_range}
                            errorText={errors.price_range}
                            className="white-bg"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0' }}>
                            <div
                              onClick={() => setFieldValue('is_member', !values.is_member)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setFieldValue('is_member', !values.is_member);
                                }
                              }}
                              style={{
                                width: '44px',
                                height: '24px',
                                backgroundColor: values.is_member ? '#ec421c' : '#dee2e6',
                                borderRadius: '12px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                userSelect: 'none'
                              }}
                              role="switch"
                              aria-checked={values.is_member}
                              aria-label="OFL Partner"
                              tabIndex={0}
                            >
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: '#fff',
                                  borderRadius: '50%',
                                  position: 'absolute',
                                  top: '2px',
                                  left: values.is_member ? '22px' : '2px',
                                  transition: 'left 0.2s'
                                }}
                              />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '500', userSelect: 'none' }}>OFL Partner</span>
                          </div>
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="website"
                            label="Website"
                            name="website"
                            type="text"
                            placeholder="Enter website URL"
                            value={values.website}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.website && !!errors.website}
                            errorText={errors.website}
                            className="white-bg"
                            autoComplete="url"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <CustomInput
                            id="overview"
                            label="Overview"
                            name="overview"
                            type="textarea"
                            placeholder="Enter restaurant overview"
                            value={values.overview}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.overview && !!errors.overview}
                            errorText={errors.overview}
                            className="white-bg"
                            rows={4}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <CustomMultiSelector
                            id="categories"
                            label="Categories"
                            name="categories"
                            placeholder="Search and select categories"
                            options={categories}
                            value={selectedCategoryIds}
                            onChange={(categoryIds) => {
                              const normalizedIds = normalizeCategoryIds(categoryIds);
                              setSelectedCategoryIds(normalizedIds);
                              if (normalizedIds.length) {
                                setCategoryError("");
                              }
                            }}
                            invalid={!!categoryError}
                            errorText={categoryError}
                            loading={loadingCategories}
                            onLoadMore={handleLoadMoreCategories}
                            hasMore={hasMoreCategories}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="custom-input-wrapper white-bg">
                            <div className={`custom-input-group ${touched.address && errors.address ? 'is-invalid' : ''}`}>
                              <Label htmlFor="address" className="custom-input-label">Address</Label>
                              <Input
                                id="address"
                                name="address"
                                type="text"
                                placeholder="Enter address (Google Places Autocomplete)"
                                value={values.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="custom-input-field"
                                innerRef={addressInputRef}
                                autoComplete="street-address"
                              />
                            </div>
                            {touched.address && errors.address && (
                              <div className="custom-input-feedback show">{errors.address}</div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={3} lg={3} md={6} sm={12}>
                          <CustomInput
                            id="city"
                            label="City"
                            name="city"
                            type="text"
                            placeholder="Enter city"
                            value={values.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.city && !!errors.city}
                            errorText={errors.city}
                            className="white-bg"
                            autoComplete="address-level2"
                          />
                        </Col>
                        <Col xl={3} lg={3} md={6} sm={12}>
                          <CustomInput
                            id="state"
                            label="State"
                            name="state"
                            type="text"
                            placeholder="Enter state"
                            value={values.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.state && !!errors.state}
                            errorText={errors.state}
                            className="white-bg"
                            autoComplete="address-level1"
                          />
                        </Col>
                        <Col xl={3} lg={3} md={6} sm={12}>
                          <CustomInput
                            id="country"
                            label="Country"
                            name="country"
                            type="text"
                            placeholder="Enter country"
                            value={values.country}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.country && !!errors.country}
                            errorText={errors.country}
                            className="white-bg"
                            autoComplete="country-name"
                          />
                        </Col>
                        <Col xl={3} lg={3} md={6} sm={12}>
                          <CustomInput
                            id="zipcode"
                            label="ZIP Code"
                            name="zipcode"
                            type="text"
                            placeholder="Enter ZIP code"
                            value={values.zipcode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.zipcode && !!errors.zipcode}
                            errorText={errors.zipcode}
                            className="white-bg"
                            autoComplete="postal-code"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="custom-input-wrapper white-bg">
                            <div className="custom-input-group">
                              <h4 className="custom-input-label" style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>Operating Hours</h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 0' }} role="group" aria-label="Operating Hours">
                                <div
                                  onClick={() => {
                                    const nextOpen24Hours = !isOpen24Hours;
                                    setIsOpen24Hours(nextOpen24Hours);
                                    if (nextOpen24Hours) {
                                      setWeekHours((prevHours) => prevHours.map(h => ({ ...h, open: '00:00', closed: '23:59', isClosed: false })));
                                      setWeekHourErrors({});
                                    }
                                  }}
                                  style={getCompactToggleTrackStyle(isOpen24Hours)}
                                  role="switch"
                                  aria-checked={isOpen24Hours}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      const nextOpen24Hours = !isOpen24Hours;
                                      setIsOpen24Hours(nextOpen24Hours);
                                      if (nextOpen24Hours) {
                                        setWeekHours((prevHours) => prevHours.map(h => ({ ...h, open: '00:00', closed: '23:59', isClosed: false })));
                                        setWeekHourErrors({});
                                      }
                                    }
                                  }}
                                >
                                  <div style={getCompactToggleThumbStyle(isOpen24Hours)} />
                                </div>
                                <span style={{ fontWeight: '500' }}>Open 24 Hours</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {!isOpen24Hours && (
                        <Row>
                          {weekHours.map((hour, index) => (
                            <Col key={hour.day} xl={3} lg={4} md={6} sm={12}>
                              <div style={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '16px', 
                                padding: '16px', 
                                marginBottom: '16px',
                                transition: 'all 0.2s ease',
                                cursor: hour.isClosed ? 'default' : 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (!hour.isClosed) {
                                  e.currentTarget.style.borderColor = '#ec421c';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(236, 66, 28, 0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              >
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>{hour.day}</div>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Input
                                      type="time"
                                      value={hour.open}
                                      onChange={(e) => {
                                        setWeekHours((prevHours) => prevHours.map((h, i) => (
                                          i === index ? { ...h, open: e.target.value } : h
                                        )));
                                      }}
                                      disabled={hour.isClosed}
                                      style={{
                                        width: '100%',
                                        backgroundColor: hour.isClosed ? '#f8f9fa' : '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onFocus={(e) => {
                                        if (!hour.isClosed) {
                                          e.currentTarget.style.borderColor = '#ec421c';
                                        }
                                      }}
                                      onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                      }}
                                      aria-label={`${hour.day} opening time`}
                                    />
                                    {weekHourErrors[getDayKey(hour.day)]?.open && (
                                      <div className="custom-input-feedback show">{weekHourErrors[getDayKey(hour.day)].open}</div>
                                    )}
                                  </div>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Input
                                      type="time"
                                      value={hour.closed}
                                      onChange={(e) => {
                                        setWeekHours((prevHours) => prevHours.map((h, i) => (
                                          i === index ? { ...h, closed: e.target.value } : h
                                        )));
                                      }}
                                      disabled={hour.isClosed}
                                      style={{
                                        width: '100%',
                                        backgroundColor: hour.isClosed ? '#f8f9fa' : '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onFocus={(e) => {
                                        if (!hour.isClosed) {
                                          e.currentTarget.style.borderColor = '#ec421c';
                                        }
                                      }}
                                      onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                      }}
                                      aria-label={`${hour.day} closing time`}
                                    />
                                    {weekHourErrors[getDayKey(hour.day)]?.closed && (
                                      <div className="custom-input-feedback show">{weekHourErrors[getDayKey(hour.day)].closed}</div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                    <div
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setWeekHours(prevHours => {
                                          const newHours = [...prevHours];
                                          const currentIsClosed = newHours[index].isClosed;
                                          newHours[index] = { ...newHours[index], isClosed: !currentIsClosed };
                                          if (!currentIsClosed) {
                                            newHours[index] = { ...newHours[index], open: '', closed: '' };
                                          }
                                          return newHours;
                                        });
                                        setWeekHourErrors((prevErrors) => {
                                          const nextErrors = { ...prevErrors };
                                          delete nextErrors[getDayKey(hour.day)];
                                          return nextErrors;
                                        });
                                      }}
                                      style={getCompactToggleTrackStyle(hour.isClosed)}
                                      role="switch"
                                      aria-checked={hour.isClosed}
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          setWeekHours(prevHours => {
                                            const newHours = [...prevHours];
                                            const currentIsClosed = newHours[index].isClosed;
                                            newHours[index] = { ...newHours[index], isClosed: !currentIsClosed };
                                            if (!currentIsClosed) {
                                              newHours[index] = { ...newHours[index], open: '', closed: '' };
                                            }
                                            return newHours;
                                          });
                                          setWeekHourErrors((prevErrors) => {
                                            const nextErrors = { ...prevErrors };
                                            delete nextErrors[getDayKey(hour.day)];
                                            return nextErrors;
                                          });
                                        }
                                      }}
                                    >
                                      <div style={getCompactToggleThumbStyle(hour.isClosed)} />
                                    </div>
                                    <span style={{ fontSize: '13px', fontWeight: '500', userSelect: 'none' }}>Closed</span>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}

                      {/* Owner/Admin Section */}
                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12} style={{ marginTop: '30px', marginBottom: '20px' }}>
                          <h2 className="profile-title">Owner/Admin Information</h2>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="primary_admin_first_name"
                            label="First Name"
                            name="primary_admin_first_name"
                            type="text"
                            placeholder="Enter first name"
                            value={values.primary_admin_first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.primary_admin_first_name && !!errors.primary_admin_first_name}
                            errorText={errors.primary_admin_first_name}
                            className="white-bg"
                            autoComplete="given-name"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="primary_admin_last_name"
                            label="Last Name"
                            name="primary_admin_last_name"
                            type="text"
                            placeholder="Enter last name"
                            value={values.primary_admin_last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.primary_admin_last_name && !!errors.primary_admin_last_name}
                            errorText={errors.primary_admin_last_name}
                            className="white-bg"
                            autoComplete="family-name"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="primary_admin_email"
                            label="Email"
                            name="primary_admin_email"
                            type="email"
                            placeholder="Enter email"
                            value={values.primary_admin_email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.primary_admin_email && !!errors.primary_admin_email}
                            errorText={errors.primary_admin_email}
                            className="white-bg"
                            autoComplete="email"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="primary_admin_mobile"
                            label="Mobile"
                            name="primary_admin_mobile"
                            type="text"
                            placeholder="Enter mobile"
                            value={values.primary_admin_mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.primary_admin_mobile && !!errors.primary_admin_mobile}
                            errorText={errors.primary_admin_mobile}
                            className="white-bg"
                            autoComplete="tel"
                          />
                        </Col>
                      </Row>

                      <div className="form-actions" style={{ marginTop: '30px' }}>
                        <SecondaryButton label="Update Restaurant" variant="orange" type="submit" disabled={loading || isSubmitting} loading={loading || isSubmitting} />
                      </div>
                    </Form>
                  )}
                </Formik>
          </div>
        </Container>
      </div>
    </>
  );
};

export default AdminRestaurantsEditPage;
